// What ONE live job on a box is using right now (#6743, epic #6552): its share of the
// whole machine's CPU, its memory working set and its checkout's size on disk. The box
// measures each from the run's own cgroup and puts one entry per live coding job on its
// presence beat (`RunnerPresenceSchema.jobs`); the Fleet card's job list reads it.
//
// ONE DEFINITION for the box and the mothership: the entry schema, its bound and the
// CPU-share arithmetic live here, and `@developerz/nats` imports them rather than
// restating them.
//
// A READING IS NEVER A ZERO BY DEFAULT. Every field is `null` when it could not be
// measured, and a CPU share outside 0..100 is `null` too, never clamped: it can only come
// from a wrong core count or a bad read, and a clamped 100 would hide that.

import { z } from 'zod';
import { TaskIdSchema } from '../../value-objects/ids';
import { RUNNER_MAX_SLOTS_MAX } from './max-slots';

/**
 * The most job entries one beat may carry: the "Jobs at once" range's own upper bound,
 * never a second literal. A box never emits more (it truncates, with a warn line), because
 * a beat that failed its own schema would not land and the box would be reaped.
 */
export const JOB_USAGE_MAX = RUNNER_MAX_SLOTS_MAX;

/** One live job's usage, as the beat carries it. */
export const JobUsageSchema = z.object({
  /** The mothership task the job holds. */
  taskId: TaskIdSchema,
  /** Share of the WHOLE machine, 0..100, one decimal. `null` = not measured (yet). */
  cpuPct: z.number().min(0).max(100).nullable(),
  /** The run's working set: `memory.current` minus reclaimable cache. */
  memBytes: z.number().int().nonnegative().nullable(),
  /** The run's checkout size (`du -sxB1`). */
  diskBytes: z.number().int().nonnegative().nullable(),
});
export type JobUsage = z.infer<typeof JobUsageSchema>;

/** One reading of a run cgroup's CPU counter: `cpu.stat` `usage_usec`, and when it was read. */
export type JobCpuReading = { readonly usageUsec: number; readonly wallMs: number };

/**
 * The job's CPU share of the whole machine between two readings:
 * `(usage delta) / (wall delta in µs x cores) x 100`, rounded to one decimal.
 *
 * `null` with no earlier reading (the first sample), when the counter went backwards,
 * when wall time did not advance, when `cores` is not a positive whole number, and when
 * the computed share falls outside 0..100 — checked on the RAW value, before rounding,
 * so 100.04 is `null` and is never rounded into range.
 */
export function jobUsage(
  prev: JobCpuReading | null,
  now: JobCpuReading | null,
  cores: number,
): number | null {
  if (prev === null || now === null) return null;
  if (!Number.isInteger(cores) || cores < 1) return null;
  const usage = now.usageUsec - prev.usageUsec;
  const wallUsec = (now.wallMs - prev.wallMs) * 1_000;
  if (usage < 0 || wallUsec <= 0) return null;
  const pct = (usage / (wallUsec * cores)) * 100;
  if (!(pct >= 0 && pct <= 100)) return null;
  return Math.round(pct * 10) / 10;
}
